// Excel (.xlsx) case export. Uses SheetJS community `xlsx`. All four
// sheets are built as arrays-of-arrays (header row + data rows) and
// converted via XLSX.utils.aoa_to_sheet so formatting stays predictable.
// Runs entirely in the browser.

import * as XLSX from "xlsx";

import type {
  Case,
  CaseRecord,
  Contact,
  KeyFact,
  Significance,
} from "../types";
import { CASE_STATUS_LABELS, SIGNIFICANCE_LABELS } from "../types";

interface Bundle {
  case: Case;
  records: CaseRecord[];
  contacts: Contact[];
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function parseKeyFacts(caseData: Case): KeyFact[] {
  if (!caseData.key_facts) return [];
  try {
    const parsed = JSON.parse(caseData.key_facts);
    return Array.isArray(parsed) ? (parsed as KeyFact[]) : [];
  } catch {
    return [];
  }
}

function typeLabelFor(r: CaseRecord): string {
  const m = r.file_mime_type ?? "";
  if (r.source_type === "email") return "Email";
  if (r.source_type === "video" || m.startsWith("video/")) return "Video";
  if (r.source_type === "phone_call" || m.startsWith("audio/")) return "Audio";
  if (m.startsWith("image/")) return "Screenshot";
  if (m === "application/pdf") return "PDF";
  if (m.includes("wordprocessingml")) return "Word";
  if (m.includes("spreadsheetml")) return "Excel";
  if (r.file_r2_key) return "File";
  if (r.source_type === "letter") return "Letter";
  if (r.source_type === "sms") return "SMS";
  if (r.source_type === "in_person") return "In person";
  return "Other";
}

function sigLabel(s: Significance): string {
  return SIGNIFICANCE_LABELS[s] ?? s;
}

// Both use UTC so exported cells match the date printed in the
// Timeline card / Records list for every row. Previously these
// used the local-timezone getters and a record stored at midnight
// UTC showed as the previous day in every row of the sheet.
import { formatTime, formatYmd } from "./formatDate";

function dateOnly(iso: string | null | undefined): string {
  return formatYmd(iso);
}

function timeOnly(iso: string | null | undefined): string {
  return formatTime(iso);
}

function wordCount(text: string | null | undefined): number {
  if (!text) return 0;
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

// SheetJS doesn't auto-size; compute per-column max content length and
// set !cols[i].wch so Excel opens the file with sensibly wide columns.
function autoWidth(rows: (string | number)[][]): { wch: number }[] {
  if (rows.length === 0) return [];
  const cols = rows[0]!.length;
  const widths: number[] = new Array(cols).fill(0);
  for (const row of rows) {
    for (let c = 0; c < cols; c++) {
      const v = row[c];
      const len = v == null ? 0 : String(v).length;
      if (len > widths[c]!) widths[c] = len;
    }
  }
  // Add a little padding; cap at 60 so a huge subject doesn't blow the
  // layout out horizontally.
  return widths.map((w) => ({ wch: Math.min(Math.max(w + 2, 8), 60) }));
}

function buildTimelineSheet(records: CaseRecord[]): XLSX.WorkSheet {
  const header: (string | number)[] = [
    "Date",
    "Time",
    "Type",
    "From",
    "To",
    "Subject / Title",
    "Significance",
    "Tags",
    "Pinned",
    "Has Attachments",
    "Attachment Count",
    "Word Count",
    "Edited",
    "Thread",
  ];
  // Map thread_id → a readable thread subject (first record in the
  // thread with a subject).
  const threadSubject = new Map<string, string>();
  for (const r of records) {
    const tid = r.thread_id?.trim();
    if (!tid) continue;
    if (!threadSubject.has(tid) && r.subject?.trim()) {
      threadSubject.set(tid, r.subject.trim());
    }
  }

  const sorted = records
    .slice()
    .sort((a, b) => a.record_date.localeCompare(b.record_date));

  const body: (string | number)[][] = sorted.map((r) => {
    const attCount = r.attachments?.length ?? 0;
    const tid = r.thread_id?.trim();
    return [
      dateOnly(r.record_date),
      timeOnly(r.record_date),
      typeLabelFor(r),
      r.from_addr ?? "",
      r.to_addr ?? "",
      r.subject ?? "",
      sigLabel(r.significance),
      (r.tags ?? "")
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean)
        .join(", "),
      r.pinned === 1 ? "Yes" : "No",
      attCount > 0 ? "Yes" : "No",
      attCount,
      wordCount(r.body_text ?? r.ocr_text ?? ""),
      r.is_edited === 1 ? "Yes" : "No",
      tid ? threadSubject.get(tid) ?? tid : "",
    ];
  });

  const rows = [header, ...body];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = autoWidth(rows);
  // Freeze the header row so it stays visible while scrolling the
  // timeline (can get long on busy cases).
  ws["!views"] = [{ state: "frozen", ySplit: 1, xSplit: 0 }];
  return ws;
}

function buildKeyFactsSheet(
  facts: KeyFact[],
  updatedAt: string | null,
): XLSX.WorkSheet {
  const header = ["Type", "Fact", "Extracted Date"];
  const body =
    facts.length === 0
      ? [["(none)", "No key facts extracted yet", ""]]
      : facts.map((f) => [
          f.type,
          f.fact,
          updatedAt ? dateOnly(updatedAt) : "",
        ]);
  const rows = [header, ...body];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = autoWidth(rows);
  ws["!views"] = [{ state: "frozen", ySplit: 1, xSplit: 0 }];
  return ws;
}

function buildContactsSheet(contacts: Contact[]): XLSX.WorkSheet {
  const header = ["Name", "Role", "Email", "Phone", "Notes", "Added Date"];
  const body: (string | number)[][] =
    contacts.length === 0
      ? [["No contacts added to this case", "", "", "", "", ""]]
      : contacts.map((c) => [
          c.name,
          c.role ?? "",
          c.email ?? "",
          c.phone ?? "",
          c.notes ?? "",
          dateOnly(c.created_at),
        ]);
  const rows = [header, ...body];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = autoWidth(rows);
  ws["!views"] = [{ state: "frozen", ySplit: 1, xSplit: 0 }];
  return ws;
}

function buildOverviewSheet(
  caseData: Case,
  records: CaseRecord[],
): XLSX.WorkSheet {
  const total = records.length;
  const kindCount = (label: string) =>
    records.filter((r) => {
      const m = r.file_mime_type ?? "";
      if (label === "Email") return r.source_type === "email";
      if (label === "Screenshot") return m.startsWith("image/");
      if (label === "Video")
        return r.source_type === "video" || m.startsWith("video/");
      return false;
    }).length;
  const pinned = records.filter((r) => r.pinned === 1).length;
  const keyRecords = records.filter((r) => r.significance === "high").length;
  const dates = records
    .map((r) => r.record_date)
    .filter((d): d is string => !!d)
    .sort();
  const firstDate = dates[0];
  const lastDate = dates[dates.length - 1];
  const summary = caseData.summary?.trim() ?? "";

  const rows: (string | number)[][] = [
    ["Field", "Value"],
    ["Case name", caseData.name],
    ["Status", CASE_STATUS_LABELS[caseData.status] ?? caseData.status],
    ["Description", caseData.description ?? ""],
    ["Created", dateOnly(caseData.created_at)],
    ["First record", firstDate ? dateOnly(firstDate) : "—"],
    ["Last activity", lastDate ? dateOnly(lastDate) : "—"],
    ["Total records", total],
    ["Email records", kindCount("Email")],
    ["Screenshot records", kindCount("Screenshot")],
    ["Video records", kindCount("Video")],
    ["Pinned records", pinned],
    ["Key records (High significance)", keyRecords],
    ["Forwarding address", caseData.forward_address],
    ["AI summary", summary || "—"],
    ["Exported", dateOnly(new Date().toISOString())],
    ["Generated by", "CaseThread"],
  ];

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = [{ wch: 30 }, { wch: 80 }];
  return ws;
}

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Reimbursement summary export. Carries the SAME data + gating the in-app view
// shows: a Status column flags unverified rows, GAP rows have a blank amount,
// and subtotals are written as live SUM() FORMULAS (not hardcoded) so the
// spreadsheet recomputes — the seeded value comes from the shared totaling
// module, so app and sheet agree exactly.

import type { ReimbursementSummary, LedgerRow } from "./reimbursementSummary";

// Build a ledger sheet (Date | Description | Paid To | Amount | Status |
// Source). Money sheets get a subtotal row whose Amount cell is a live SUM()
// FORMULA (seeded from the same cents the app shows — no drift). Unvalued
// rows leave the Amount cell BLANK; the Source column distinguishes verbatim-
// from-source rows from manually-entered ones so a computed row never looks
// identically authoritative.
function buildLedgerSheet(
  rows: LedgerRow[],
  opts: { withSubtotal: boolean },
): XLSX.WorkSheet {
  const header = ["Date", "Description", "Paid To", "Amount", "Status", "Source"];
  const aoa: (string | number | null)[][] = [header];
  for (const r of rows) {
    aoa.push([
      r.date ?? "",
      r.description,
      r.party ?? "",
      r.amountCents === null ? null : r.amountCents / 100,
      r.status ?? "(needs status)",
      r.provenance === "manual"
        ? "manual entry"
        : r.amountEdited
          ? "from source (amount edited)"
          : "verbatim from source",
    ]);
  }
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  // Unvalued rows → truly blank Amount cell (SheetJS renders array null as 0).
  rows.forEach((r, i) => {
    if (r.amountCents === null) delete ws[`D${i + 2}`];
  });
  let lastRow = rows.length + 1;
  if (opts.withSubtotal && rows.length > 0) {
    const subtotalRow = rows.length + 2;
    const cached = rows.reduce((s, r) => s + (r.amountCents ?? 0), 0) / 100;
    XLSX.utils.sheet_add_aoa(ws, [["", "Subtotal", "", null, "", ""]], { origin: `A${subtotalRow}` });
    ws[`D${subtotalRow}`] = { t: "n", f: `SUM(D2:D${rows.length + 1})`, v: cached };
    lastRow = subtotalRow;
  }
  if (rows.length > 0) ws["!ref"] = `A1:F${lastRow}`;
  ws["!cols"] = [{ wch: 12 }, { wch: 50 }, { wch: 20 }, { wch: 13 }, { wch: 16 }, { wch: 26 }];
  ws["!views"] = [{ state: "frozen", ySplit: 1, xSplit: 0 }];
  return ws;
}

function buildSummaryContactsSheet(s: ReimbursementSummary): XLSX.WorkSheet {
  // Validated, verbatim contact values only — no guessed party. The user
  // assigns the party when they add it to the case contact book.
  const header = ["Type", "Value (verbatim from source)", "In contact book"];
  const aoa: (string | number)[][] = [header];
  for (const c of s.contacts) {
    aoa.push([c.type, c.value, c.promoted ? "yes" : ""]);
  }
  if (s.contacts.length === 0) aoa.push(["", "No phone/email found in sources", ""]);
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = [{ wch: 8 }, { wch: 34 }, { wch: 16 }];
  ws["!views"] = [{ state: "frozen", ySplit: 1, xSplit: 0 }];
  return ws;
}

// Build the workbook (no DOM) so it can be unit-tested by reading cells back.
export function buildReimbursementWorkbook(
  s: ReimbursementSummary,
): XLSX.WorkBook {
  const wb = XLSX.utils.book_new();

  const overview: (string | number)[][] = [
    ["Field", "Value"],
    ["Case", s.header.caseName],
    ["Records", s.header.recordCount],
    ["First activity", s.header.firstDate?.slice(0, 10) ?? "—"],
    ["Last activity", s.header.lastDate?.slice(0, 10) ?? "—"],
    ["— To resolve —", ""],
    ["Need a status", s.completeness.needStatus],
    ["Possible duplicates", s.completeness.possibleDups],
    ["Graduations to confirm", s.completeness.graduationsOffered],
    ["TBD without an amount", s.completeness.tbdUnvalued],
    ["Total to resolve", s.completeness.total],
    ["Exported", new Date().toISOString().slice(0, 10)],
  ];
  const ovWs = XLSX.utils.aoa_to_sheet(overview);
  ovWs["!cols"] = [{ wch: 24 }, { wch: 50 }];
  XLSX.utils.book_append_sheet(wb, ovWs, "Financials");

  XLSX.utils.book_append_sheet(wb, buildLedgerSheet(s.payments.rows, { withSubtotal: true }), "Payments received");
  XLSX.utils.book_append_sheet(wb, buildLedgerSheet(s.outOfPocket.rows, { withSubtotal: true }), "Out-of-pocket");
  XLSX.utils.book_append_sheet(wb, buildLedgerSheet(s.tbd, { withSubtotal: false }), "TBD");
  if (s.documentedUncosted.length > 0)
    // Confirmed items with no price (Status = Documented — uncosted); NOT in any
    // subtotal. Travels to the export so the understatement is documented too.
    XLSX.utils.book_append_sheet(wb, buildLedgerSheet(s.documentedUncosted, { withSubtotal: false }), "Documented-uncosted");
  if (s.resolvedNoCost.length > 0)
    // Documented amount kept for the narrative; Status = Waived / no cost; NOT
    // in any subtotal (no withSubtotal), consistent with the in-app section.
    XLSX.utils.book_append_sheet(wb, buildLedgerSheet(s.resolvedNoCost, { withSubtotal: false }), "Resolved-no cost");
  if (s.needsReview.length > 0)
    XLSX.utils.book_append_sheet(wb, buildLedgerSheet(s.needsReview, { withSubtotal: false }), "Needs review");
  XLSX.utils.book_append_sheet(wb, buildSummaryContactsSheet(s), "Contacts");
  return wb;
}

export async function exportReimbursementXlsx(
  s: ReimbursementSummary,
): Promise<void> {
  const buf = XLSX.write(buildReimbursementWorkbook(s), {
    bookType: "xlsx",
    type: "array",
  });
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const dateSlug = new Date().toISOString().slice(0, 10);
  triggerDownload(blob, `CaseThread Financials - ${s.header.caseName} - ${dateSlug}.xlsx`);
}

export async function exportCaseAsXlsx(bundle: Bundle): Promise<void> {
  const { case: caseData, records, contacts } = bundle;
  const keyFacts = parseKeyFacts(caseData);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, buildTimelineSheet(records), "Timeline");
  XLSX.utils.book_append_sheet(
    wb,
    buildKeyFactsSheet(keyFacts, caseData.key_facts_updated_at ?? null),
    "Key Facts",
  );
  XLSX.utils.book_append_sheet(wb, buildContactsSheet(contacts), "Contacts");
  XLSX.utils.book_append_sheet(
    wb,
    buildOverviewSheet(caseData, records),
    "Case Overview",
  );

  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const dateSlug = new Date().toISOString().slice(0, 10);
  const filename = `CaseThread - ${caseData.name} - ${dateSlug}.xlsx`;
  triggerDownload(blob, filename);
}
